<template>
  <div>
    <!-- 검색 -->
    <search-nav @handleSearch="onClickSearch">
      <div class="search-layout w-full grid col-gap-4 flex-wrap xl:flex">
        <component
          v-for="(component, index) in stdSearchComponents"
          :key="`search-${index}`"
          :is="component['component']"
          :label="component['label']"
          :query="component['query']"
          :options="component['options']"
          :onClickSearch="onClickSearch"
          v-model="nav.value[component['value']]"
        />
      </div>
    </search-nav>

    <div class="flex items-center justify-between mb-4 flex-wrap gap-y-2">
      <div class="flex items-center gap-4">
        <vs-select v-model="perPage">
          <vs-select-item text="10개씩 보기" :value="10" />
          <vs-select-item text="20개씩 보기" :value="20" />
          <vs-select-item text="50개씩 보기" :value="50" />
          <vs-select-item text="100개씩 보기" :value="100" />
        </vs-select>
        <span class="total-count">표시된 인원 : {{ filteredList.length }}명</span>
      </div>
      <vs-button color="secondary" type="border" @click="onClickDownload">Excel 다운로드</vs-button>
    </div>

    <!-- 로딩 -->
    <div v-if="loading" class="mb-4">
      <vs-progress indeterminate color="primary" />
    </div>

    <!-- 목록 -->
    <div class="table-wrap" ref="tableWrap">
      <div class="table-scroll">
        <table>
        <colgroup>
          <col style="width: 70px" />
          <col style="width: 120px" />
          <col style="width: 130px" />
          <col style="width: 110px" />
          <col style="width: 90px" />
          <col style="width: 100px" />
          <col style="width: 130px" />
          <col style="width: 140px" />
          <col style="width: 90px" />
          <col style="width: 140px" />
        </colgroup>
        <thead>
          <tr>
            <th rowspan="2">연도</th>
            <th rowspan="2">사업장명</th>
            <th rowspan="2">부서명</th>
            <th rowspan="2">파트명</th>
            <th rowspan="2">직급</th>
            <th rowspan="2">이름</th>
            <th colspan="3">3S Credit</th>
            <th rowspan="2">비고</th>
          </tr>
          <tr>
            <th>가점<br />(세이프티 카드)</th>
            <th>감점<br />(안전위반통보서)</th>
            <th>합산</th>
          </tr>
        </thead>
        <tbody v-for="row in paginatedList" :key="row.key">
          <tr :class="{ 'row-active': detail.show && detail.row && detail.row.key === row.key }">
            <td>{{ row.연도 }}</td>
            <td>{{ row.사업장명 }}</td>
            <td>{{ row.부서명 }}</td>
            <td>{{ row.파트명 }}</td>
            <td>{{ row.직급 }}</td>
            <td>{{ row.이름 }}</td>
            <td class="clickable" :class="{ active: isDetailActive(row, '가점') }" @click="openDetail(row, '가점', $event)">
              {{ row.가점 > 0 ? `+${row.가점}` : "-" }}
            </td>
            <td class="clickable" :class="{ active: isDetailActive(row, '감점') }" @click="openDetail(row, '감점', $event)">
              {{ 0 > row.감점 ? row.감점 : "-" }}
            </td>
            <td class="sum-cell">{{ row.합산 > 0 ? `+${row.합산}` : row.합산 }}</td>
            <td>
              <span v-if="row.심의대상" class="review-target">안전보건위원회 심의 대상</span>
              <span v-else>-</span>
            </td>
          </tr>
        </tbody>
        <tbody v-if="!loading && paginatedList.length === 0">
          <tr>
            <td colspan="10">데이터가 없습니다.</td>
          </tr>
        </tbody>
        </table>
      </div>

      <!-- 가점/감점 상세 팝업 -->
      <div v-if="detail.show" ref="detailPopup" class="detail-popup" :style="detail.style">
        <div class="detail-popup-header">
          <div>
            <span class="detail-title-person">{{ detail.row.이름 }}</span>
            <span class="detail-title-info">({{ detail.row.사업장명 }}<template v-if="detail.row.부서명"> · {{ detail.row.부서명 }}</template><template v-if="detail.row.파트명"> · {{ detail.row.파트명 }}</template><template v-if="detail.row.직급"> · {{ detail.row.직급 }}</template>)</span>
            {{ detail.type === "가점" ? "가점 내역 (세이프티 카드)" : "감점 내역 (안전위반통보서)" }}
          </div>
          <feather-icon icon="XIcon" svgClasses="h-5 w-5" class="detail-close" title="닫기" @click="closeDetail" />
        </div>
        <div class="detail-popup-body">
          <table class="inner-table">
            <colgroup>
              <col style="width: 110px" />
              <col style="width: 110px" />
              <col style="width: 150px" />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th>발급일자</th>
                <th>등급</th>
                <th>{{ detail.type === "가점" ? "발급유형" : "위반유형" }}</th>
                <th>세부내용</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, itemIndex) in detailItems" :key="`detail-${itemIndex}`" :class="gradeRowClass(item.등급)">
                <td>{{ item.발급일자 }}</td>
                <td>
                  <span class="grade-badge" :style="{ background: 등급뱃지색[item.등급] || '#999' }">{{ item.등급 }}</span>
                  ({{ item.점수 > 0 ? `+${item.점수}` : item.점수 }}점)
                </td>
                <td>{{ item.유형 }}</td>
                <td class="text-left">{{ item.세부내용 }}</td>
              </tr>
              <tr v-if="detailItems.length === 0">
                <td colspan="4">내역이 없습니다.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="vs-con-table">
      <div class="con-pagination-table vs-table--pagination">
        <k-pagination :total="lastPage" v-model="page"></k-pagination>
      </div>
    </div>

    <popup-excel-download v-model="download" :list="excelList" title="3S Credit 현황" />
  </div>
</template>

<script>
import SearchNav from "@/components/nav/SearchNav.vue";
import searchComponent from "@/views/list/components/include";
import KPagination from "@/components/kPagination/kPagination.vue";
import PopupExcelDownload from "@/popup/PopupExcelDownload.vue";

// 가점(Safety Card) / 감점(안전위반통보서) 게시판
const SAFETY_CARD_BOARD_ID = 1228;
const VIOLATION_BOARD_ID = 1229;

const 가점점수 = { 개선: 1, 우수: 2, 탁월: 3 };
const 감점점수 = { 경미: -1, 주의: -2, 심각: -3 };

export default {
  components: {
    SearchNav,
    KPagination,
    PopupExcelDownload,
  },
  props: {
    board: Object,
    header: Object,
    permission: Object,
  },
  computed: {
    projectId() {
      return parseInt(this.$route.params.proj_id);
    },
    userInfo() {
      return this.$store.state.auth.userInfo || {};
    },
    isAdmin() {
      return parseInt(this.$store.state.member.memberInfo.is_superuser) > 0;
    },
    // 안전환경부서와 관리자는 전체 조회, 그 외(직영 일반)는 본인(대상자 기준) 것만 조회 - 2026-09 고객 확정
    // 914: 본사(안전환경기획팀) / 915: 각 공장 및 연구소(안전환경팀) — 916 관리감독자/917 일반직원은 본인 것만
    canViewAll() {
      if (this.isAdmin) return true;
      const companyId = parseInt(this.userInfo.company_id);
      return [914, 915].includes(companyId);
    },
    stdSearchComponents() {
      const components = [
        {
          component: "search-year",
          label: "연도",
          value: "year",
          query: { y: "year" },
          options: {
            default: new Date().getFullYear(),
            afterFix: "년",
          },
        },
        {
          component: "search-select-field",
          label: "사업장명",
          value: "사업장명",
          query: { f_id: "fieldId", f_name: "fieldName" },
          options: {
            default: "",
          },
        },
        {
          component: "search-start-end",
          label: "집계기간",
          value: "date",
          query: { ss: "startDate", ee: "endDate" },
          options: {
            default: { sd: "", ed: "" },
          },
        },
        {
          component: "search-keyword",
          label: "부서명",
          value: "부서명",
          query: { dept: "keyword" },
          options: {
            default: "",
          },
        },
        {
          component: "search-keyword",
          label: "이름",
          value: "이름",
          query: { nm: "keyword" },
          options: {
            default: "",
          },
        },
      ];

      for (const component of components) {
        component.component = searchComponent[component["component"]];
      }
      return components;
    },

    // 원시 데이터 → 개별 발급 건 목록
    allItems() {
      const items = [];
      const pushItem = (data, type) => {
        const content = data.postContents ? data.postContents : data.post_content ? JSON.parse(data.post_content) : {};
        const body = content.body || {};
        const 대상자 = body.대상자 || {};
        const 등급 = body.등급 || "";
        const 점수 = type === "가점" ? 가점점수[등급] || 0 : 감점점수[등급] || 0;
        items.push({
          type,
          사업장명: (body.stdInfo && body.stdInfo.field_name) || "",
          대상자id: 대상자.id || 0,
          사번: 대상자.사번 || "",
          이름: 대상자.이름 || "",
          부서명: 대상자.부서명 || "",
          파트명: 대상자.파트명 || "",
          직급: 대상자.직급 || "",
          등급,
          점수,
          발급일자: body.발급일자 || "",
          유형: type === "가점" ? body.발급유형 || "" : (body.위반내역 && body.위반내역.유형) || body.위반유형 || "",
          세부내용: type === "가점" ? body.세부설명 || "" : (body.위반내역 && body.위반내역.세부내용) || body.세부설명 || "",
        });
      };
      this.plusRawData.forEach((data) => pushItem(data, "가점"));
      this.minusRawData.forEach((data) => pushItem(data, "감점"));
      return items;
    },

    // 검색조건(집계기간/사업장/부서명/이름) + 본인 제한을 적용한 발급 건 목록
    filteredItems() {
      const search = this.nav.value;
      let list = this.allItems;

      // 직영(비 안전환경부서)은 본인 내역만
      if (!this.canViewAll) {
        list = list.filter((item) => this.isMyItem(item));
      }

      // 집계기간 (승인된 문서에 작성된 발급일자 기준)
      if (search.date && search.date.startDate) {
        list = list.filter((item) => item.발급일자 >= search.date.startDate);
      }
      if (search.date && search.date.endDate) {
        const nextDay = new Date(search.date.endDate).addDays(1).format("yyyy-MM-dd");
        list = list.filter((item) => item.발급일자 < nextDay);
      }

      // 사업장명
      if (search.사업장명 && search.사업장명.fieldName) {
        list = list.filter((item) => item.사업장명.includes(search.사업장명.fieldName));
      }

      // 부서명
      if (search.부서명) {
        list = list.filter((item) => item.부서명.includes(search.부서명));
      }

      // 이름
      if (search.이름) {
        list = list.filter((item) => item.이름.includes(search.이름));
      }

      return list;
    },

    // 인원별 합산 목록
    filteredList() {
      const map = {};
      for (const item of this.filteredItems) {
        const key = item.사번 || (item.대상자id ? `id${item.대상자id}` : `${item.이름}|${item.부서명}`);
        if (!map[key]) {
          map[key] = {
            key,
            연도: this.nav.value.year,
            사업장명: item.사업장명,
            부서명: item.부서명,
            파트명: item.파트명,
            직급: item.직급,
            이름: item.이름,
            가점: 0,
            감점: 0,
            합산: 0,
            심의대상: false,
            가점상세: [],
            감점상세: [],
          };
        }
        const row = map[key];
        // 인적정보는 최신 발급 건 기준으로 갱신
        if (item.사업장명) row.사업장명 = item.사업장명;
        if (item.부서명) row.부서명 = item.부서명;
        if (item.파트명) row.파트명 = item.파트명;
        if (item.직급) row.직급 = item.직급;

        const detail = {
          발급일자: item.발급일자,
          등급: item.등급,
          점수: item.점수,
          유형: item.유형,
          세부내용: item.세부내용,
        };
        if (item.type === "가점") {
          row.가점 += item.점수;
          row.가점상세.push(detail);
        } else {
          row.감점 += item.점수;
          row.감점상세.push(detail);
        }
      }

      const rows = Object.values(map);
      for (const row of rows) {
        row.합산 = row.가점 + row.감점;
        // 선택 연도(발급일자 기준, 매년 1월 리셋) 합산수치가 -3점 이하인 경우 심의 대상
        row.심의대상 = row.합산 <= -3;
        row.가점상세.sort((a, b) => {
          return a.발급일자 < b.발급일자 ? 1 : -1;
        });
        row.감점상세.sort((a, b) => {
          return a.발급일자 < b.발급일자 ? 1 : -1;
        });
      }

      rows.sort((a, b) => {
        if (a.사업장명 !== b.사업장명) return a.사업장명.localeCompare(b.사업장명);
        if (a.부서명 !== b.부서명) return a.부서명.localeCompare(b.부서명);
        return a.이름.localeCompare(b.이름);
      });

      return rows;
    },

    lastPage() {
      return Math.max(1, Math.ceil(this.filteredList.length / this.perPage));
    },

    paginatedList() {
      const start = (this.page - 1) * this.perPage;
      return this.filteredList.slice(start, start + this.perPage);
    },

    excelList() {
      return this.filteredList.map((row) => {
        return {
          "연도": row.연도,
          "사업장명": row.사업장명,
          "부서명": row.부서명,
          "파트명": row.파트명,
          "직급": row.직급,
          "이름": row.이름,
          "가점(세이프티 카드)": row.가점 > 0 ? `+${row.가점}` : "-",
          "감점(안전위반통보서)": 0 > row.감점 ? `${row.감점}` : "-",
          "합산": row.합산 > 0 ? `+${row.합산}` : `${row.합산}`,
          "비고": row.심의대상 ? "안전보건위원회 심의 대상" : "-",
        };
      });
    },

    // 상세 팝업에 표시할 내역 목록
    detailItems() {
      if (!this.detail.show || !this.detail.row) return [];
      return this.detail.type === "가점" ? this.detail.row.가점상세 : this.detail.row.감점상세;
    },
  },
  data() {
    return {
      nav: {
        value: {
          year: new Date().getFullYear(),
        },
      },
      plusRawData: [],
      minusRawData: [],
      loading: false,
      page: 1,
      perPage: 20,
      download: false,
      detail: {
        show: false,
        row: null,
        type: "",
        style: {},
      },
      // 상세 팝업 등급 뱃지 색상 (가점: Safety Card / 감점: 안전위반통보서)
      등급뱃지색: {
        개선: "#6da9e8",
        우수: "#e0b25a",
        탁월: "#e26b6b",
        경미: "#00CFE8",
        주의: "#FF9F43",
        심각: "#EA5455",
      },
    };
  },
  mounted() {
    // 팝업 외부 클릭 시 닫기
    document.addEventListener("click", this.onDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick);
  },
  async created() {
    // 관리자/부서 판별용 회원 상세정보 로드
    if (!this.$store.state.member.memberInfo || !this.$store.state.member.memberInfo.id) {
      try {
        await this.$store.dispatch("member/LOAD_MEMBER_DETAIL");
      } catch (e) {
        /* ignore */
      }
    }
  },
  watch: {
    "nav.value.year": {
      immediate: true,
      handler() {
        this.loadAllFromApi();
      },
    },
    perPage() {
      this.page = 1;
      this.closeDetail();
    },
    page() {
      this.closeDetail();
    },
    lastPage(value) {
      if (this.page > value) this.page = value;
    },
  },
  methods: {
    isMyItem(item) {
      const userInfo = this.userInfo;
      if (item.대상자id && parseInt(item.대상자id) === parseInt(userInfo.id)) return true;
      if (item.사번 && (item.사번 === userInfo.username || item.사번 === userInfo.nickname)) return true;
      return false;
    },
    onClickSearch() {
      this.page = 1;
      this.closeDetail();
    },
    onDocumentClick(event) {
      if (!this.detail.show) return;
      const popup = this.$refs.detailPopup;
      if (popup && popup.contains(event.target)) return;
      // 가점/감점 셀 클릭은 openDetail 이 토글로 처리하므로 제외
      if (event.target.closest && event.target.closest("td.clickable")) return;
      this.closeDetail();
    },
    onClickDownload() {
      this.download = true;
    },
    isDetailActive(row, type) {
      return this.detail.show && this.detail.row && this.detail.row.key === row.key && this.detail.type === type;
    },
    gradeRowClass(grade) {
      if (grade === "개선" || grade === "경미") return "grade-blue";
      if (grade === "우수" || grade === "주의") return "grade-orange";
      if (grade === "탁월" || grade === "심각") return "grade-red";
      return "";
    },
    openDetail(row, type, event) {
      // 같은 셀을 다시 클릭하면 닫기
      if (this.isDetailActive(row, type)) {
        this.closeDetail();
        return;
      }

      // 클릭한 셀 바로 아래(가운데 정렬)에 팝업 표시, 테이블 영역을 벗어나지 않게 클램프
      let top = 40;
      let left = 8;
      try {
        const wrapRect = this.$refs.tableWrap.getBoundingClientRect();
        const cellRect = event.currentTarget.getBoundingClientRect();
        const popupWidth = Math.min(640, wrapRect.width - 16);
        top = cellRect.bottom - wrapRect.top + 6;
        left = cellRect.left - wrapRect.left + (cellRect.width / 2) - (popupWidth / 2);
        left = Math.max(8, Math.min(left, wrapRect.width - popupWidth - 8));
      } catch (e) {
        /* ignore */
      }
      this.detail.style = { top: `${top}px`, left: `${left}px` };
      this.detail.row = row;
      this.detail.type = type;
      this.detail.show = true;
    },
    closeDetail() {
      this.detail.show = false;
      this.detail.row = null;
      this.detail.type = "";
    },

    // 연도 변경 시에만 API 호출 (결재완료 건만 조회, 연도 귀속은 승인된 문서에 작성된 발급일자 기준 — 매년 1월 리셋)
    async loadAllFromApi() {
      this.loading = true;
      const year = this.nav.value.year;
      const [plus, minus] = await Promise.all([
        this.loadBoardAll(SAFETY_CARD_BOARD_ID, year, ["body.stdInfo.field_name", "body.대상자%", "body.등급", "body.발급유형", "body.세부설명", "body.발급일자"]),
        this.loadBoardAll(VIOLATION_BOARD_ID, year, ["body.stdInfo.field_name", "body.대상자%", "body.등급", "body.위반내역%", "body.세부설명", "body.발급일자"]),
      ]);
      this.plusRawData = plus;
      this.minusRawData = minus;
      this.page = 1;
      this.closeDetail();
      this.loading = false;
    },

    async loadBoardAll(boardId, year, metaKeys) {
      try {
        // 결재 API 를 statusIds: [4](승인)로 조회하여 결재완료 건만 집계한다
        const baseOptions = {
          projectId: this.projectId,
          boardSlug: `board-${boardId}`,
          perPage: 200,
          statusIds: [4], // 1:temp 2:registered 3:ongoing 4:approved 5:rejected
          conditions: [
            // 연도 귀속은 발급일자(작성자 입력) 기준: 발급일자가 해당 연도인 건만 집계
            { key: "body.발급일자", operator: "LIKE", value: `${year}-%` },
          ],
          withMeta: true,
          onlyMeta: true,
          metaKeys,
        };

        const firstResponse = await this.$store.dispatch("approval/LOAD_POST_ALL", { ...baseOptions, page: 1 });
        let allData = [...firstResponse.data];
        if (firstResponse.last_page > 1) {
          const pagePromises = [];
          for (let p = 2; p <= firstResponse.last_page; p++) {
            pagePromises.push(this.$store.dispatch("approval/LOAD_POST_ALL", { ...baseOptions, page: p }));
          }
          const remainingResponses = await Promise.all(pagePromises);
          for (const response of remainingResponses) {
            allData = allData.concat(response.data);
          }
        }
        return allData;
      } catch (e) {
        // 게시판이 아직 없거나 조회 실패 시 빈 목록으로 처리 (1229 미개발 상태 대비)
        return [];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.total-count {
  font-size: 13px;
  color: #666;
}
.table-wrap {
  position: relative;
}
.table-scroll {
  overflow-x: auto;
}
table {
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;

  th,
  td {
    border: 1px solid #cdcdcd;
    padding: 12px 7px;
  }

  th {
    font-size: 14px;
    font-weight: 500;
    text-align: center;
    background: #f9f9f9;
  }

  td {
    font-size: 14px;
    font-weight: 400;
    text-align: center;
    background: white;
  }

  .clickable {
    cursor: pointer;
    font-weight: 600;
    color: #5b3cc4;

    &:hover {
      background: #f3f0ff;
    }

    &.active {
      background: #ede9fe;
    }
  }

  .sum-cell {
    font-weight: 600;
    background: #fdf6ec;
  }

  .review-target {
    color: #ea5455;
    font-weight: 600;
  }

  /* 팝업이 열려 있는 행 하이라이트 */
  .row-active td {
    background: #f3f0ff;
  }
}

/* 가점/감점 상세 팝업 */
.detail-popup {
  position: absolute;
  /* vuesax 페이지네이션 버튼이 z-index 200 이라 그보다 위로 */
  z-index: 1000;
  width: 640px;
  max-width: calc(100% - 16px);
  background: #fff;
  border: 1px solid #d5d5d5;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  overflow: hidden;

  .detail-popup-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    padding: 12px 14px;

    .detail-title-person {
      color: #5b3cc4;
      font-size: 14px;
    }

    .detail-title-info {
      font-weight: 400;
      color: #777;
      margin-right: 4px;
    }

    .detail-close {
      color: #888;
      cursor: pointer;
      flex-shrink: 0;

      &:hover {
        color: #ea5455;
      }
    }
  }

  .detail-popup-body {
    padding: 0 14px 14px;
    max-height: 320px;
    overflow-y: auto;
  }

  .inner-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 0;

    th,
    td {
      border: 1px solid #e0e0e0;
      padding: 8px 7px;
      font-size: 13px;
      text-align: center;
    }

    th {
      background: #f9f9f9;
      font-weight: 500;
    }

    .text-left {
      text-align: left;
    }

    .grade-badge {
      display: inline-block;
      color: #fff;
      font-size: 12px;
      font-weight: 600;
      border-radius: 4px;
      padding: 2px 8px;
    }

    tr.grade-blue td {
      background: #eaf2fc;
    }
    tr.grade-orange td {
      background: #fdf3e6;
    }
    tr.grade-red td {
      background: #fdecec;
    }
  }
}
</style>
